/**
 * 操作ログ「旧」一覧（GET …/operation-log）。`/operation-log/legacy` 専用。
 * 詳細 API は使わない（一覧の列のみ）。
 * 廃止時: 本ファイル・legacy-page・legacy-search・legacy-row・Redux/API の legacy 系・本ルートを削除。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useWindowSize } from 'react-use'
import { css } from '@emotion/core'
import { isEqual } from 'lodash'

import { Button } from '@/components/atoms/button'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { dashboardPageContainerStyle, tableHeaderThStyle } from '@/constants/layout'
import { OperationLogLegacyFiltersType } from '@/models/operation-log'
import { OperationLogLegacyRow } from '@/components/organisms/operation-log/legacy-row'
import { OperationLogLegacySearch } from '@/components/organisms/operation-log/legacy-search'
import { LoadingFull } from '@/components/molecules/loading-full'
import { useAppDispatch, useAppSelector } from '@/features/hooks'
import { fetchOperationLogLegacyList } from '@/features/operation-log/operation-log-thunks'
import { hasOperationLogNextPage } from './has-operation-log-next-page'
import {
  operationLogLegacyQueryNeedsNormalize,
  parseOperationLogLegacyQuery,
  stringifyOperationLogLegacyQuery,
} from '@/utils/operation-log-url'
import { operationLogListScrollStyle, operationLogTableStyle } from '@/components/organisms/operation-log/table-cell-styles'
import { useScrollToTop } from '@/hooks/use-scroll-to-top'

const limit = 50

export const OperationLogLegacyScreen: React.FC = () => {
  const windows = useWindowSize()
  const history = useHistory()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const dispatch = useAppDispatch()
  const legacyList = useAppSelector(s => s.operationLog.legacyList)
  const legacyListStatus = useAppSelector(s => s.operationLog.legacyListStatus)
  const legacyPage = useAppSelector(s => s.operationLog.legacyPage)
  const legacyPerPage = useAppSelector(s => s.operationLog.legacyPerPage)
  const legacyFilters = useAppSelector(s => s.operationLog.legacyFilters)
  const legacyListMeta = useAppSelector(s => s.operationLog.legacyListMeta)
  const legacyListLinks = useAppSelector(s => s.operationLog.legacyListLinks)
  const { scrollRef: listScrollRef, scrollToTop: scrollListToTop } = useScrollToTop()

  const [isOpenDetailedSearch, setIsOpenDetailedSearch] = useState(false)
  const loading = legacyListStatus === 'loading'

  const hasNextPage = useMemo(
    () => hasOperationLogNextPage(legacyListMeta, legacyListLinks, legacyList.length, legacyPerPage),
    [legacyListMeta, legacyListLinks, legacyList.length, legacyPerPage],
  )

  useEffect(() => {
    const { filters: qFilters, page: qPage } = parseOperationLogLegacyQuery(location.search)
    if (operationLogLegacyQueryNeedsNormalize(location.search, qFilters, qPage)) {
      const qs = stringifyOperationLogLegacyQuery(qFilters, qPage)
      history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' })
      return
    }
    dispatch(fetchOperationLogLegacyList({ filters: qFilters, page: qPage, limit }))
      .unwrap()
      .then(() => scrollListToTop())
      .catch(err => errorHandler(err))
  }, [dispatch, errorHandler, history, location.pathname, location.search, scrollListToTop])

  const onChangeLegacySearchFilters = (data: OperationLogLegacyFiltersType) => {
    if (!isEqual(data, legacyFilters)) {
      const qs = stringifyOperationLogLegacyQuery(data, 1)
      history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' })
    }
    setIsOpenDetailedSearch(false)
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage === legacyPage) return
      const qs = stringifyOperationLogLegacyQuery(legacyFilters, newPage)
      history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' })
    },
    [history, legacyFilters, legacyPage, location.pathname],
  )

  return (
    <div css={[dashboardPageContainerStyle, { position: 'relative' }]}>
      <LoadingFull isLoading={loading} />
      <div css={headerSettingOperationLogStyle}>
        <div css={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <div css={headerDetailStyle}>{t('Operation history')}</div>
          <Button
            buttonType={4}
            width={i18n.language === 'en' ? 118 : 90}
            height={32}
            fontSize={12}
            marginLeft={24}
            marginRight={12}
            onClick={() => setIsOpenDetailedSearch(!isOpenDetailedSearch)}
          >
            {t('Detailed search')}
          </Button>
          <Link to="/operation-log" css={screenNavLinkStyle}>
            {t('OperationLog.navToNew')}
          </Link>
        </div>
        <AccommodationPaging page={Number(legacyPage)} setPage={handlePageChange} hasNextPage={hasNextPage} limit={legacyPerPage} />
      </div>
      <div css={{ display: isOpenDetailedSearch ? 'block' : 'none' }}>
        <OperationLogLegacySearch onChange={onChangeLegacySearchFilters} initialFilters={legacyFilters} />
      </div>
      <div ref={listScrollRef} css={[operationLogListScrollStyle, { height: windows.height - 154 }]}>
        {!!legacyList.length && (
          <table css={operationLogTableStyle}>
            <thead>
              <tr>
                <th css={[tableHeaderThStyle, { paddingLeft: 28, width: '5%' }]} />
                <th css={[tableHeaderThStyle, { width: '18%' }]}>{t('Date and time of operation')}</th>
                <th css={[tableHeaderThStyle, { width: '4%' }]}>{t('Operation')}</th>
                <th css={[tableHeaderThStyle, { width: '11%' }]}>{t('Features')}</th>
                <th css={[tableHeaderThStyle, { width: '34%' }]}>{t('Table')}</th>
                <th css={[tableHeaderThStyle, { width: '16%' }]}>{t('Account')}</th>
                <th css={[tableHeaderThStyle, { width: '12%', paddingRight: 28 }]}>{t('Staff')}</th>
              </tr>
            </thead>
            <tbody>
              {legacyList.map(item => (
                <OperationLogLegacyRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        )}
        {!legacyList.length && !loading && (
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <h1>{t('No data available')}</h1>
          </div>
        )}
      </div>
    </div>
  )
}

const headerSettingOperationLogStyle = css({
  height: '50px',
  padding: '0 32px',
  borderBottom: '1px solid #F2F2F2',
  display: 'flex',
  justifyContent: 'space-between',
})

const headerDetailStyle = css({
  fontSize: '18px',
  fontWeight: 'bold',
  padding: '16px 0',
  color: '#272727',
})

const screenNavLinkStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 8,
  minHeight: 32,
  padding: '0 16px',
  borderRadius: 24,
  fontSize: 12,
  fontWeight: 'bold',
  backgroundColor: '#fff',
  border: '1px solid #CCCCCC',
  color: '#272727',
  textDecoration: 'none',
  boxSizing: 'border-box',
  '&:hover': {
    opacity: 0.88,
  },
})
